// Copyright (c) 2018 Cigent Technology Inc. All Rights Reserved.

import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import {
  ISubscriptions, ISubscription, ILicense, IActivationToken,
  ISoftwareVersion, ISubAllocationResponse, MaxEndpointPageSize, IEndpointExportToken, LicensesUsed
} from 'src/types/subscription';
import {
  ICreateIntegrationResponse,
  IValidationResponse,
  IIntegration,
  IIntegrationsResponse,
  ISophosTenantsResponse,
  ISentinelOneValidationResponse,
  IIntegrationStatusResponse
} from 'src/types/integrations';
import {Observable, of, throwError} from 'rxjs';
import { API_URL } from 'src/shared/api';
import { map, catchError } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { IDevice } from 'src/types/subscription';
import {
  ID3EPolicy,
  ID3EPolicyResponse,
  ID3EPolicyRequest,
  IPolicyListItem,
  IEffectivePolicyResponse,
  ICertificateSubjectResponse,
  IPolicyFeatureFlags,
  IHomeConfig,
  IPolicyCreatedResponse,
  IEndPointsConfigResponse, 
  IForcePolicyResetRequest,
  IEndpointActionData,
  ConsoleAction
} from 'src/types/d3ePolicy';
import {IDeviceSetGetResponse, IDeviceSetRequest, IDeviceSetPostResponse, IDeviceSetBare, IDeviceSetsGroup} from 'src/types/device-sets';
import { IIntegrationsPageConfig } from 'src/types/integrations';
import {HomeAlertsResponse} from 'src/app/notifications-models';
import {ICount} from 'src/types/common';
import {IDeviceActiveLocks} from '../types/device-details';
import {
  IHistoryConfig,
  IThreatHistoryResponse,
  IThreatCsvToken,
  ITopThreatDevice,
  ITopThreat,
  IActivityResponse,
  ISensorTypesResponse
} from '../types/threat';
import {
  IDiskConfigResponse,
  IDriveResponse,
  IProvisionDriveInfo,
  IProvisionDrivesRequest,
  IProvisioningTemplate, IVolumeConfigResponse
} from '../types/drive';
import {ConfigurationService} from './configuration-service';
import {ExpiringSubscriptionsResponse} from '../app/pages/admin/expiring_subs';
import {IPartnerListResponse} from '../types/partners';
import {
    IDashboardOverviewResponse,
    IDashboardProtectionResponse, IEndpointsLocationRequest, IEndpointsLocationResponse
} from '../app/pages/dashboard/dashboard.types';
import { IEndpointsConnected, IEndpointsLocked, IEndpointsResponse, IEndpointTotals } from '../types/endpoints';
import {ALL_ORGS_ID} from '../types/group';
import {
    IAddWorkstationsRequest,
    IAssignedDrivesResponse, IBulkUpdatePbaSerialRequest, IFdeLicenseResponse, IFdeQuery,
    INewPbaSerialResponse,
    IUpdatePbaSerialRequest
} from '../types/licensedDrives';
import { IPbaUser, IFdeUser } from '../types/d3ePolicy';
import { IAddEnrollCodesResponse, IEnrollCodesDialogResult } from 'src/app/shared/components/enroll-codes-dialog/enroll-codes-dialog.component';
import { IDeleteEnrollCodesRequest } from 'src/app/shared/components/action-confirmation-dialog/action-confirmation-dialog.component';

export interface IAllocateParams {
  source_id: number;
  target_id: number;
  count: number;
  target_group_id: number;
  subscription_start: string;
  subscription_end: string;
  maintenance_end: string;
  max_duration_days: number;
  notify: boolean;
  notifyLanguage: string;
  sub_type: number;
  enablePBA_Features: boolean;
  enablePBALogErasure: boolean;
}

export interface ILicensesUsedResponse {
  data: LicensesUsed;
}

@Injectable({
  providedIn: 'root'
})
export class D3eService {

  constructor(
    private http: HttpClient,
    private translate: TranslateService,
    private configurationService: ConfigurationService,
  ) { }

  getUrl(suffix) {
    return `${this.configurationService.apiUrl}${suffix}`;
  }

  get_subscriptions(selectedOrgId = '', searchTerm = '', showMspSubs = false): Observable<ISubscription[]> {
    let url = this.getUrl(API_URL.SUBSCRIPTIONS);
    const params = [];
    if (!!selectedOrgId && selectedOrgId !== ALL_ORGS_ID) {
      params.push('selectedOrgId=' + encodeURIComponent(selectedOrgId));
    }
    if (!!searchTerm) {
      params.push('searchTerm=' + encodeURIComponent(searchTerm));
    }
    if (showMspSubs) {
      params.push('showMspSubs=enabled')
    }
    if (params.length) {
      const queryString = params.join('&');
      url += '?' + queryString;
    }
    return this.http.get<ISubscriptions>(url)
        .pipe(
            map(result => result.subscriptions.sort((a, b) => b.sub_timestamp > a.sub_timestamp ? 1 : -1)),
            catchError(this.handleError.bind(this)
    ));
  }

  getLicensesUsed(): Observable<LicensesUsed> {
    const url = this.getUrl('/api/licenses-used');
    return this.http.get<ILicensesUsedResponse>(url).pipe(map(r => r.data));
  }

  get_subscriptions_expiring(days_future: number, days_past: number): Observable<ExpiringSubscriptionsResponse> {
    const url = this.getUrl(`${API_URL.SUBSCRIPTIONS_EXPIRING}?days_future=${days_future}&days_past=${days_past}`);
    return this.http.get<ExpiringSubscriptionsResponse>(url)
        .pipe(catchError(this.handleError.bind(this)));
  }

  extendSubscription(subscription_id, new_exp_date): Observable<void> {
    const url = this.getUrl(`${API_URL.SUBSCRIPTIONS_EXTEND}/${subscription_id}`);
    const request = {"exp_date": new_exp_date};
    return this.http.put<void>(url, request);
  }

  add_activation_token(activationToken: IActivationToken): Observable<string> {
    const body = { 'activation_token': activationToken };
    const url = this.getUrl(API_URL.ACTIVATION_TOKENS);
    return this.http.post<string>(url, body)
      .pipe(catchError(this.handleError.bind(this)));
  }

  move_license(subscriptionId, uuid): Observable<string> {
    const url = this.getUrl(`${API_URL.SUBSCRIPTION}/${subscriptionId}${API_URL.LICENSE}/${uuid}`);
    return this.http.put<string>(url, {})
      .pipe(catchError(this.handleError.bind(this)));
  }

  deactivate_license(subscriptionId, uuid): Observable<string> {
    const url = this.getUrl(`${API_URL.SUBSCRIPTION}/${subscriptionId}${API_URL.LICENSE}/${uuid}`);
    return this.http.delete<string>(url, {})
      .pipe(catchError(this.handleError.bind(this)));
  }

  subscription_allocate(allocateParams: IAllocateParams): Observable<ISubAllocationResponse> {
    const body = {
      source_id: allocateParams.source_id,
      target_id: allocateParams.target_id,
      count: allocateParams.count,
      target_group_id: allocateParams.target_group_id,
      subscription_start: allocateParams.subscription_start,
      subscription_end: allocateParams.subscription_end,
      maintenance_end: allocateParams.maintenance_end,
      max_duration_days: allocateParams.max_duration_days,
      notify_admins_by_email: allocateParams.notify,
      notify_language: allocateParams.notifyLanguage,
      sub_type: allocateParams.sub_type,
      enable_pba_extended_features: allocateParams.enablePBA_Features,
      enable_pba_log_erase: allocateParams.enablePBALogErasure
    };
    const url = this.getUrl(API_URL.SUBSCRIPTION_ALLOCATE);
    return this.http.post<ISubAllocationResponse>(url, body)
        .pipe(catchError(this.handleError.bind(this)));
  }

  get_d3e_software_version(subUUID = ''): Observable<ISoftwareVersion> {
    const url = `${this.getUrl(API_URL.API_V1_DOWNLOADS)}?sub_uuid=${subUUID}`;
    return this.http.get(url)
        .pipe(catchError(this.handleError.bind(this)));
  }

  get_d3e_version_history(term): Observable<{'versions': ISoftwareVersion[]}> {
    const url = this.getUrl(`${API_URL.D3E_VERSION_HISTORY}?term=${term}`);
    return this.http.get<{'versions': ISoftwareVersion[]}>(url)
        .pipe(catchError(this.handleError.bind(this)));
  }

  // Manage Integrations ============

  getIntegrationsConfig(): Observable<IIntegrationsPageConfig> {
    const url = this.getUrl(API_URL.INTEGRATIONS_CONFIG);
    return this.http.get<IIntegrationsPageConfig>(url);
  }

  get_integrations(selectedOrgId = ''): Observable<IIntegration[]> {
    let url = this.getUrl(API_URL.INTEGRATIONS_LIST);
    if ( selectedOrgId.length > 0 ) {
      url += '?selectedOrgId=' + selectedOrgId;
    }
    return this.http.get<IIntegration>(url)
        .pipe(catchError(this.handleError.bind(this)));
  }

  get_integration(id: string, selectedOrgId = ''): Observable<IIntegration> {
    let url = this.getUrl(`${API_URL.INTEGRATIONS_GET}/${id}`);
    if ( selectedOrgId.length > 0 ) {
      url += '?selectedOrgId=' + selectedOrgId;
    }
    return this.http.get<IIntegration>(url)
        .pipe(catchError(this.handleError.bind(this)));
  }

  add_integration(integration: IIntegration, selectedOrgId = ''): Observable<ICreateIntegrationResponse> {
    let url = this.getUrl(API_URL.INTEGRATIONS_CREATE);
    if ( selectedOrgId.length > 0 ) {
      url += '?selectedOrgId=' + selectedOrgId;
    }
    return this.http.post<ICreateIntegrationResponse>(url, integration);
  }

  get_integration_token(id: string, invalidate: boolean = false, selectedOrgId = ''): Observable<{token: string}> {
    let url = this.getUrl(`${API_URL.INTEGRATIONS_GET_TOKEN}/${id}`);
    let argAdded = false;
    if (invalidate) {
      url = `${url}?invalidate=yes`;
      argAdded = true;
    }
    if ( selectedOrgId.length > 0 ) {
      if (!argAdded) {
        url += '?';
      }
      url += 'selectedOrgId=' + selectedOrgId;
    }

    return this.http.get<IIntegration>(url)
        .pipe(catchError(this.handleError.bind(this)));
  }

  get_active_integrations(): Observable<IIntegrationsResponse> {
    const url = this.getUrl(API_URL.INTEGRATIONS_ADMIN);
    return this.http.get<IIntegrationsResponse>(url)
        .pipe(catchError(this.handleError.bind(this)));
  }

  update_integration(integration: IIntegration, selectedOrgId = ''): Observable<IIntegration[]> {
    let url = this.getUrl(`${API_URL.INTEGRATIONS_UPDATE}/${integration.id}`);
    if ( selectedOrgId.length > 0 ) {
      url += '?selectedOrgId=' + selectedOrgId;
    }
    return this.http.put<IIntegration>(url, integration)
        .pipe(catchError(this.handleError.bind(this)));
  }

  delete_integration(integration_id: string, selectedOrgId = ''): Observable<IIntegration[]> {
    let url = this.getUrl(`${API_URL.INTEGRATIONS_DELETE}/${integration_id}`);
    if (selectedOrgId.length > 0) {
      url += '?selectedOrgId=' + selectedOrgId;
    }
    return this.http.delete<IIntegration>(url)
        .pipe(catchError(this.handleError.bind(this)));
  }

  set_integration_enabled(id: string, enabled: boolean, selectedOrgId = ''): Observable<string> {
    const request = {enabled};
    let url = this.getUrl(`${API_URL.INTEGRATIONS_UPDATE}/${id}/enabled`);
    if (selectedOrgId.length > 0) {
      url += '?selectedOrgId=' + selectedOrgId;
    }
    return this.http.put<string>(url, request)
        .pipe(catchError(this.handleError.bind(this)));
  }

  get_integration_status(id: string, selectedOrgId = ''): Observable<IIntegrationStatusResponse> {
    let url = this.getUrl(`/integration/${id}/status`);
    if (selectedOrgId.length > 0) {
      url += '?selectedOrgId=' + selectedOrgId;
    }
    return this.http.get<IIntegrationStatusResponse>(url);
  }

  get_event_log_monitors(selectedOrgId = ''): Observable<{sensor_ids: number[]}> {
    let url = this.getUrl(`${API_URL.EVENT_LOG_MONITORS}`);
    if ( selectedOrgId.length > 0 ) {
      url += '?selectedOrgId=' + selectedOrgId;
    }
    return this.http.get<{sensor_ids: number[]}>(url);
  }

  set_event_log_monitor_enabled(sensorType: number, enabled: boolean, selectedOrgId = ''): Observable<string> {
    const request = {sensor_type: sensorType, enabled: enabled};
    let url = this.getUrl(API_URL.EVENT_LOG_MONITOR_ENABLED);
    if (selectedOrgId.length > 0) {
      url += '?selectedOrgId=' + selectedOrgId;
    }
    return this.http.post<void>(url, request)
        .pipe(catchError(this.handleError.bind(this)));
  }

  validate_cyberark_config(cyberarkUrl, username, password, appId): Observable<IValidationResponse> {
    const data = {cyberarkUrl, username, password, appId};
    const url = this.getUrl(API_URL.VALIDATE_CYBERARK);
    return this.http.post<IValidationResponse>(url, data);
  }

  validate_carbon_black_config(config): Observable<IValidationResponse> {
    const url = this.getUrl(API_URL.VALIDATE_CARBON_BLACK);
    return this.http.post<IValidationResponse>(url, config);
  }

  get_sophos_tenants(client_id: string, client_secret: string): Observable<ISophosTenantsResponse> {
    const data = {client_id, client_secret};
    const url = this.getUrl(API_URL.GET_SOPHOS_TENANTS);
    return this.http.post<ISophosTenantsResponse>(url, data);
  }

  validate_sentinel_one_config(apiUrl: string, apiToken: string): Observable<ISentinelOneValidationResponse> {
    const url = this.getUrl(API_URL.VALIDATE_SENTINEL_ONE);
    const data = {
      api_url: apiUrl,
      api_token: apiToken,
    }
    return this.http.post<ISentinelOneValidationResponse>(url, data);
  }

  // Manage Groups (previously called Device Sets) ===============

  getDeviceSet(id: string, selectedOrgId = ''): Observable<IDeviceSetGetResponse> {
    let url = this.getUrl(`${API_URL.DEVICE_SETS_GET}/${id}`);
    if ( selectedOrgId.length > 0 ) {
      url += '?selectedOrgId=' + selectedOrgId;
    }
    return this.http.get<IDeviceSetGetResponse>(url)
        .pipe(catchError(this.handleError.bind(this)));
  }

  createDeviceSet(request: IDeviceSetRequest, selectedOrgId = ''): Observable<IDeviceSetPostResponse> {
    let url = this.getUrl(API_URL.DEVICE_SETS_CREATE);
    if ( selectedOrgId.length > 0 ) {
      url += '?selectedOrgId=' + selectedOrgId;
    }
    return this.http.post<IDeviceSetPostResponse>(url, request)
        .pipe(catchError(this.handleError.bind(this)));
  }

  updateDeviceSet(id: string, request: IDeviceSetRequest, selectedOrgId = ''): Observable<string> {
    let url = this.getUrl(`${API_URL.DEVICE_SETS_UPDATE}/${id}`);
    if ( selectedOrgId.length > 0 ) {
      url += '?selectedOrgId=' + selectedOrgId;
    }
    return this.http.put<string>(url, request)
        .pipe(catchError(this.handleError.bind(this)));
  }

  deleteDeviceSet(id: string, selectedOrgId= ''): Observable<string> {
    let url = this.getUrl(`${API_URL.DEVICE_SETS_DELETE}/${id}`);
    if ( selectedOrgId.length > 0 ) {
      url += '?selectedOrgId=' + selectedOrgId;
    }
    return this.http.delete<string>(url)
        .pipe(catchError(this.handleError.bind(this)));
  }

  getDeviceSets(selectedOrgId = ''): Observable<IDeviceSetBare[]> {
    let url = this.getUrl(API_URL.DEVICE_SETS_LIST);
    if ( selectedOrgId.length > 0 ) {
      url += '?selectedOrgId=' + selectedOrgId;
    }
    return this.http.get<IDeviceSetBare[]>(url)
        .pipe(catchError(this.handleError.bind(this)));
  }

  getDeviceSetGroups(selectedOrgId = ''): Observable<IDeviceSetsGroup[]> {
    let url = this.getUrl(API_URL.DEVICE_SETS_LIST);
    if ( selectedOrgId.length > 0 ) {
      url += '?selectedOrgId=' + selectedOrgId;
    }
    return this.http.get<IDeviceSetsGroup[]>(url)
        .pipe(catchError(this.handleError.bind(this)));
  }

  get_home_config(selectedOrgId = ''): Observable<IHomeConfig> {
    let url = this.getUrl(API_URL.HOME_CONFIG);
    if (selectedOrgId.length > 0 ) {
      url += '?selectedOrgId=' + selectedOrgId;
    }
    return this.http.get<IHomeConfig>(url)
      .pipe(catchError(this.handleError.bind(this)
      ));
  }

  get_endpoints_location(query: IEndpointsLocationRequest, selectedOrgId = ''): Observable<IEndpointsLocationResponse> {
    let url = this.getUrl('/api/v1/endpoints/location');
    if ( selectedOrgId.length > 0 ) {
      url += '?selectedOrgId=' + selectedOrgId;
    }
    return this.http.post(url, query)
        .pipe(catchError(this.handleError.bind(this)));
  }

  getEndpointsRelativeChanges(selectedOrgId = ''): Observable<IEndpointTotals> {
    let url = this.getUrl(`/api/v1/endpoints/relative-changes`);
    if ( selectedOrgId.length > 0 ) {
      url += '?selectedOrgId=' + selectedOrgId;
    }
    return this.http.get<IEndpointTotals>(url)
      .pipe(catchError(this.handleError.bind(this)));
  }

  get_home_devices(query): Observable<IEndpointsResponse> {
    const pageSize = query['pageSize'];
    if (pageSize === undefined || pageSize > MaxEndpointPageSize ) {
      query['pageSize'] = MaxEndpointPageSize;
    }
    const pageStart = query['pageStart'];
    if (pageStart === undefined) {
      query['pageStart'] = 0;
    }
    const url = this.getUrl(`${API_URL.HOME_DEVICES}/query`);
    return this.http.post<IEndpointsResponse>(url, query)
      .pipe(catchError(this.handleError.bind(this)));
  }

  getEndpointsConnected(selectedOrgId = ''): Observable<IEndpointsConnected> {
    let url = this.getUrl(`/api/v1/endpoints/connected`);
    if ( selectedOrgId.length > 0 ) {
      url += '?selectedOrgId=' + selectedOrgId;
    }
    return this.http.get<IEndpointsConnected>(url)
      .pipe(catchError(this.handleError.bind(this)));
  }

  getEndpointsLocked(selectedOrgId = ''): Observable<IEndpointsLocked> {
    let url = this.getUrl(`/api/v1/endpoints/locked`);
    if ( selectedOrgId.length > 0 ) {
      url += '?selectedOrgId=' + selectedOrgId;
    }
    return this.http.get<IEndpointsLocked>(url)
      .pipe(catchError(this.handleError.bind(this)));
  }


  delete_endpoint(deviceUuid: string): Observable<string> {
    const url = this.getUrl(`${API_URL.API_V1_ENDPOINT}/${deviceUuid}`);
    return this.http.delete<string>(url)
      .pipe(catchError(this.handleError.bind(this)));
  }

  get_endpoints_csv_token(query): Observable<IEndpointExportToken> {
    const url = this.getUrl('/home/devices/export/token');
    return this.http.post(url, query)
      .pipe(catchError(this.handleError.bind(this)));
  }

  get_home_device(device_id: string): Observable<IDevice> {
    const url = this.getUrl(`${API_URL.HOME_DEVICE}/${device_id}`);
    return this.http.get<IDevice>(url)
      .pipe(catchError(this.handleError.bind(this)));
  }

  get_home_device_active_locks(device_id: string): Observable<IDeviceActiveLocks> {
    const url = this.getUrl(`${API_URL.HOME_DEVICE}/${device_id}/activelocks`);
    return this.http.get<IDeviceActiveLocks>(url)
      .pipe(catchError(this.handleError.bind(this)));
  }

  get_home_device_overview(query): Observable<IDashboardOverviewResponse> {
    const url = this.getUrl('/home/dashboard/deviceOverview');
    return this.http.post(url, query)
        .pipe(catchError(this.handleError.bind(this)));
  }

  get_home_device_config_dashboard(query): Observable<IDashboardProtectionResponse> {
    const url = this.getUrl('/home/dashboard/deviceConfig');
    return this.http.post(url, query)
        .pipe(catchError(this.handleError.bind(this)));
  }

  get_effective_policy(device_id: string): Observable<IEffectivePolicyResponse> {
    const url = this.getUrl(`${API_URL.HOME_EFFECTIVE_POLICY}/${device_id}`);
    return this.http.get<ILicense>(url)
      .pipe(catchError(this.handleError.bind(this)));
  }


  post_threat(active: boolean, license_uuid: string, sensorType: number = 1001): Observable<Number> {
    const url = this.getUrl(API_URL.API_POST_THREATS);
    const json_body = {
      'threats': [
          { 'deviceId': license_uuid,
            'sensorType': sensorType, // 1001 is console
            'responseType': 2, // 2 is RESP_ACTIVELOCK
            'enabled': active,
            'allowDeactivate': true,
            'description': 'This alert was generated manually by a user from the Console'
          }
      ]
    };
    return this.http.post<IDevice>(url, json_body)
      .pipe(catchError(this.handleError.bind(this)
      ));
  }

  get_home_alerts(limit: number): Observable<HomeAlertsResponse> {
    const url = this.getUrl(`${API_URL.HOME_ALERTS}?limit=${limit}`);
    return this.http.get<HomeAlertsResponse>(url).pipe(
      catchError(() => {
        return of(null);
      })
    );
  }

  clear_alert(id: number): Observable<string> {
    const url = this.getUrl(API_URL.HOME_ALERT);
    return this.http.put<string>(url, {id: id});
  }

  clear_all_alerts(): Observable<string> {
    const url = this.getUrl(API_URL.HOME_ALERTS_CLEAR);
    return this.http.post<string>(url, null);
  }

  force_sync_config_and_state(license_uuid: string): Observable<string> {
    const url = this.getUrl(API_URL.API_FORCE_SYNC);
    const json_body = {'uuid':  license_uuid};
    return this.http.post<string>(url, json_body)
      .pipe(catchError(this.handleError.bind(this)
      ));
  }

  force_policy_reset(forcePolicyResetRequest: IForcePolicyResetRequest): Observable<string> {
    const url = this.getUrl(`${API_URL.API_FORCE_POLICY_REST}/force-reset`);
    return this.http.post<string>(url, forcePolicyResetRequest)
      .pipe(catchError(this.handleError.bind(this)
      ));
  }

  // Manage Policies
  getD3EPolicies(selectedOrgId = ''): Observable<IPolicyListItem[]> {
    let url = this.getUrl(API_URL.API_POLICIES_D3E);
    if ( selectedOrgId.length > 0 ) {
      url += '?selectedOrgId=' + selectedOrgId;
    }
    return this.http.get(url)
        .pipe(catchError(this.handleError.bind(this)));
  }

  getGlobalD3EPolicy(id, selectedOrgId = ''): Observable<ID3EPolicyResponse> {
    let url = this.getUrl(`${API_URL.API_POLICY_D3E}/${id}`);
    if ( selectedOrgId.length > 0 ) {
      url += '?selectedOrgId=' + selectedOrgId;
    }
    return this.http.get(url)
      .pipe(catchError(this.handleError.bind(this)
      ));

  }

  postD3EPolicy(name: string, policy: {}, selectedOrgId = '', clonePbaUsersFrom = undefined): Observable<IPolicyCreatedResponse> {
    let url = this.getUrl(API_URL.API_POLICY_D3E_POST);
    if ( selectedOrgId.length > 0 ) {
      url += '?selectedOrgId=' + selectedOrgId;
    }
    return this.http.post<IPolicyCreatedResponse>(url, {
      name: name,
      policy: policy,
      clonePbaUsersFrom: clonePbaUsersFrom,
    });
  }

  renameD3EPolicy(id: number, name: string, selectedOrgId = ''): Observable<{}> {
    let url = this.getUrl(`${API_URL.API_POLICY_D3E}/${id}/rename`);
    if ( selectedOrgId.length > 0 ) {
      url += '?selectedOrgId=' + selectedOrgId;
    }
    return this.http.put(url, {name: name});
  }

  deleteD3EPolicy(id: number, selectedOrgId = ''): Observable<string> {
    let url = this.getUrl(`${API_URL.API_POLICY_D3E_DELETE}/${id}`);
    if ( selectedOrgId.length > 0 ) {
      url += '?selectedOrgId=' + selectedOrgId;
    }
    return this.http.delete<string>(url);
  }

  setD3EPolicyPriorities(ids: number[], selectedOrgId = ''): Observable<string> {
    let url = this.getUrl(API_URL.API_POLICY_D3E_PRIORITY);
    if ( selectedOrgId.length > 0 ) {
      url += '?selectedOrgId=' + selectedOrgId;
    }
    return this.http.put<string>(url, {ids});
  }

  cloneD3EPolicy(policyId: number, newName: string, selectedOrgId = ''): Observable<IPolicyCreatedResponse> {
    let url = this.getUrl(API_URL.API_POLICY_D3E_CLONE);
    if ( selectedOrgId.length > 0 ) {
      url += '?selectedOrgId=' + selectedOrgId;
    }
    const request = {
      cloneFrom: policyId,
      name: newName,
    };
    return this.http.post<IPolicyCreatedResponse>(url, request);
  }

  setGlobalD3EPolicy(id, policyRequest: ID3EPolicyRequest, selectedOrgId = '') {
    let url = this.getUrl(`${API_URL.API_POLICY_D3E}/${id}`);
    if ( selectedOrgId.length > 0 ) {
      url += '?selectedOrgId=' + selectedOrgId;
    }
    return this.http.put<ID3EPolicy>(url, policyRequest)
      .pipe(catchError(this.handleError.bind(this)));
  }

  getDeviceSetsForPolicy(id: number, selectedOrgId = ''): Observable<string[]> {
    let url = this.getUrl(`${API_URL.API_POLICY_D3E}/${id}/device-sets`);
    if ( selectedOrgId.length > 0 ) {
      url += '?selectedOrgId=' + selectedOrgId;
    }
    return this.http.get<string[]>(url);
  }

  getSubjectForCertificate(file: File): Observable<ICertificateSubjectResponse> {
    const url = this.getUrl(API_URL.API_POLICY_CERTIFICATE);
    const formData = new FormData();
    formData.append('filename', file, file?.name);
    return this.http.post<ICertificateSubjectResponse>(url, formData);
  }

  getPolicyPageConfig(): Observable<IPolicyFeatureFlags> {
    const url = this.getUrl(API_URL.API_POLICY_CONFIG);
    return this.http.get<IPolicyFeatureFlags>(url);
  }

  get_history_config(selectedOrgId = ''): Observable<IHistoryConfig> {
    let url = this.getUrl('/history/config');
    if ( selectedOrgId.length > 0 ) {
      url += '?selectedOrgId=' + selectedOrgId;
    }
    return this.http.get(url)
    .pipe(catchError(this.handleError.bind(this)));
  }

  get_threats(query): Observable<IThreatHistoryResponse> {
    const url = this.getUrl('/history/threats');
    return this.http.post(url, query)
      .pipe(catchError(this.handleError.bind(this)));
  }

  get_threats_csv_token(query): Observable<IThreatCsvToken> {
    const url = this.getUrl('/history/threats/csv/token');
    return this.http.post(url, query)
      .pipe(catchError(this.handleError.bind(this)));
  }

  getTopNDevices(query): Observable<ITopThreatDevice[]> {
    const url = this.getUrl('/history/TopNDevices');
    return this.http.post(url, query)
        .pipe(catchError(this.handleError.bind(this)));
  }

  getTopNThreats(query): Observable<ITopThreat[]> {
    const url = this.getUrl('/history/TopNThreats');
    return this.http.post(url, query)
        .pipe(catchError(this.handleError.bind(this)));
  }

  get_activity(query): Observable<IActivityResponse> {
    const url = this.getUrl('/history/dashboard/activity');
    return this.http.post(url, query)
        .pipe(catchError(this.handleError.bind(this)));
  }

  getSensorTypes(selectedOrgId = ''): Observable<ISensorTypesResponse> {
    let url = this.getUrl(API_URL.SENSOR_TYPES);
    if ( selectedOrgId.length > 0 ) {
      url += '?selectedOrgId=' + selectedOrgId;
    }
    return this.http.get(url)
        .pipe(catchError(this.handleError.bind(this)));
  }

  getEndpointsConfig(selectedOrgId = ''): Observable<IEndPointsConfigResponse> {
    let url = this.getUrl('/endpoints/config');
    if (selectedOrgId.length > 0 ) {
      url += '?selectedOrgId=' + selectedOrgId;
    }
    return this.http.get(url)
      .pipe(catchError(this.handleError.bind(this)));
  }

  assignDeviceSet(selectedOrgId = '', license_id_list, device_set_id): Observable<string> {
    let url = this.getUrl('/device-set/assign');
    if ( selectedOrgId.length > 0 ) {
      url += '?selectedOrgId=' + selectedOrgId;
    }
    const json_body = { 'licenses': license_id_list,
                        'deviceSetId': device_set_id };
    return this.http.post(url, json_body)
      .pipe(catchError(this.handleError.bind(this)));
  }

  lockEndpoints(selectedOrgId = '',
                licenseIdList: number[],
                enabled: boolean,
                allowDeactivate: boolean): Observable<ICount> {
    let url = this.getUrl('/endpoints/manualLock');
    if ( selectedOrgId.length > 0 ) {
      url += '?selectedOrgId=' + selectedOrgId;
    }
    const json_body = {
      licenses: licenseIdList,
      allow_deactivate: allowDeactivate,
      enabled: enabled,
    };
    return this.http.post(url, json_body)
      .pipe(catchError(this.handleError.bind(this)));
  }

  lockEndpointsByQuery(selectedOrgId = '', query, enabled: boolean, allowDeactivate: boolean): Observable<ICount> {
    const url = this.getUrl('/endpoints/manualLock/byquery');
    query['selectedOrgId'] = selectedOrgId;
    const json_body = {
      query: query,
      allow_deactivate: allowDeactivate,
      enabled: enabled
    };
    return this.http.post(url, json_body)
      .pipe(catchError(this.handleError.bind(this)));
  }

  applyPriorityLock(organizations, engage: boolean): Observable<any> {
    const url = this.getUrl('/endpoints/prioritylock');
    const body = { 'organizations': organizations,
                   'engage': engage};
    return this.http.post<string>(url, body)
        .pipe(catchError(this.handleError.bind(this)));
  }

   resetEndpoint(selectedOrgId: string, fde_license_id) : Observable<any> {
      let url = this.getUrl('/endpoints/reset');
      if ( selectedOrgId.length > 0 ) {
        url += '?selectedOrgId=' + selectedOrgId;
      }
      const json_body = {'license': fde_license_id}
          return this.http.post(url, json_body)
      .pipe(catchError(this.handleError.bind(this)));
    }


  getDrives(selectedOrgId: string = '', query): Observable<IDriveResponse> {
    let url = this.getUrl('/drives');
    if ( selectedOrgId.length > 0 ) {
      url += '?selectedOrgId=' + selectedOrgId;
    }
    return this.http.post<IDriveResponse>(url, query)
      .pipe(catchError(this.handleError.bind(this)));
  }

  getDiskConfig(requestId: number, disk_serial: string): Observable<IDiskConfigResponse> {
    const url = this.getUrl('/drives/diskconfig/' + requestId.toString());
    return this.http.get<IDriveResponse>(url)
      .pipe(catchError(this.handleError.bind(this)));

  }


  getVolumeConfig(requestId: number): Observable<IVolumeConfigResponse> {
    const url = this.getUrl('/drives/volumeconfig/' + requestId.toString());
    return this.http.get<IDriveResponse>(url)
      .pipe(catchError(this.handleError.bind(this)));
  }

  createProvisioningTemplate(selectedOrgId: string = '', template: IProvisioningTemplate): Observable<string> {
    let url = this.getUrl(API_URL.API_PROVISIONING_TEMPLATE);
    if ( selectedOrgId.length > 0 ) {
      url += '?selectedOrgId=' + selectedOrgId;
    }
    return this.http.post<string>(url, template);
  }

  updateProvisioningTemplate(selectedOrgId: string = '', template: IProvisioningTemplate): Observable<string> {
    let url = this.getUrl(API_URL.API_PROVISIONING_TEMPLATE);
    if ( selectedOrgId.length > 0 ) {
      url += '?selectedOrgId=' + selectedOrgId;
    }
    return this.http.put<string>(url, template);
  }

  getProvisioningTemplate(selectedOrgId: string = ''): Observable<IProvisioningTemplate> {
    let url = this.getUrl(`${API_URL.API_PROVISIONING_TEMPLATE}`);
    if ( selectedOrgId.length > 0 ) {
      url += '?selectedOrgId=' + selectedOrgId;
    }
    return this.http.get<IProvisioningTemplate>(url);
  }

  provisionDrives(selectedOrgId: string = '', drives: IProvisionDrivesRequest) {
    let url = this.getUrl(API_URL.API_PROVISION_DRIVES);
    if ( selectedOrgId.length > 0 ) {
      url += '?selectedOrgId=' + selectedOrgId;
    }
    return this.http.post<string>(url, drives);
  }

  requestInstallPba(selectedOrgId: string = '', endpointIds: number[]): Observable<any> {
    let url = this.getUrl(API_URL.API_INSTALL_PBA);
    if (!!selectedOrgId) {
      url += '?selectedOrgId=' + selectedOrgId;
    }
    return this.http.post(url, {licenses: endpointIds});
  }

  requestWipePba(selectedOrgId: string = '', endpointIds: number[]): Observable<any> {
    let url = this.getUrl(API_URL.API_WIPE_PBA);
    if (!!selectedOrgId) {
      url += '?selectedOrgId=' + selectedOrgId;
    }
    return this.http.post(url, {licenses: endpointIds});
  }

  requestDisablePba(selectedOrgId: string = '', endpointIds: number[]): Observable<any> {
    let url = this.getUrl(API_URL.API_DISABLE_PBA);
    if (!!selectedOrgId) {
      url += '?selectedOrgId=' + selectedOrgId;
    }
    return this.http.post(url, {licenses: endpointIds});
  }

  cancelPendingProvisions(selectedOrgId: string = '', driveInfos: IProvisionDriveInfo[]) {
    let url = this.getUrl(API_URL.API_PROVISION_DRIVES);
    if ( selectedOrgId.length > 0 ) {
      url += '?selectedOrgId=' + selectedOrgId;
    }
    const options = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
      body: {
        drives: driveInfos
      },
    };
    return this.http.delete<string>(url, options);
  }

  getPartners(): Observable<IPartnerListResponse> {
    const url = this.getUrl('/api/partners');
    return this.http.get<IPartnerListResponse>(url);
  }

  getPbaSerials(selectedOrgId: string = '', subscription_id, query) : Observable<IAssignedDrivesResponse> {
    let url = this.getUrl('/api/pba/licenses/')
    url += subscription_id
    if ( selectedOrgId.length > 0 ) {
      url += '?selectedOrgId=' + selectedOrgId;
    }
    if ( query == null ) {
      query = {}
    }

    return this.http.post(url, query).pipe(catchError(this.handleError.bind(this)));
  }

  addPbaSerial(selectedOrgId: string = '', subscription_id, newSerials) : Observable<INewPbaSerialResponse> {
      let url = this.getUrl('/api/pba/serial')
      if ( selectedOrgId.length > 0 ) {
        url += '?selectedOrgId=' + selectedOrgId;
      }
      const request = {
        'new_serials': newSerials,
        'subscription_id': subscription_id
      }
      return this.http.post(url, request).pipe(catchError(this.handleError.bind(this)));
  }

  updatePbaSerial(selectedOrgId: string = '', pbaSerialRequest: IUpdatePbaSerialRequest) : Observable<string> {
      let url = this.getUrl('/api/pba/serial')
      if ( selectedOrgId.length > 0 ) {
        url += '?selectedOrgId=' + selectedOrgId;
      }
      return this.http.put(url, pbaSerialRequest).pipe(catchError(this.handleError.bind(this)));
  }

  bulkUpdatePbaSerial(selectedOrgId: string = '', pbaSerialRequest: IBulkUpdatePbaSerialRequest) : Observable<string> {
      let url = this.getUrl('/api/pba/serial/bulk')
      if ( selectedOrgId.length > 0 ) {
        url += '?selectedOrgId=' + selectedOrgId;
      }
      return this.http.put(url, pbaSerialRequest).pipe(catchError(this.handleError.bind(this)));
  }

  uploadRequestCode(selectedOrgId: string = '', subscription_id, request_code) : Observable<IAssignedDrivesResponse> {
    let url = this.getUrl('/api/pba/upload')
    if ( selectedOrgId.length > 0 ) {
      url += '?selectedOrgId=' + selectedOrgId;
    }
    const request = {
      'request_code': request_code,
      'subscription_id': subscription_id
    }

    return this.http.post(url, request).pipe(catchError(this.handleError.bind(this)));
  }

  generatePbaLicense(selectedOrgId: string, subscription_id: string, maintEnd: string, selectedSerials: string[]) {
    let url = this.getUrl('/api/pba/generate')
    if ( selectedOrgId.length > 0 ) {
      url += '?selectedOrgId=' + selectedOrgId;
    }
    const request = {
      'subscription_id': subscription_id,
      'disk_serials': selectedSerials,
      'maintEnd': maintEnd,
    }
    return this.http.post(url, request).pipe(catchError(this.handleError.bind(this)));
  }

  deletePbaLicense(selectedOrgId: string, subscription_id: string, selectedSerial: string) {
    let url = this.getUrl(`/api/pba/serial/${subscription_id}/${selectedSerial}`)
    if ( selectedOrgId.length > 0 ) {
        url += '?selectedOrgId=' + selectedOrgId;
    }
    return this.http.delete(url).pipe(catchError(this.handleError.bind(this)));
  }

  getPbaLicenseDetails(selectedOrgId, subscriptionId, diskSerial) {
    let url = this.getUrl(`/api/pba/details/${subscriptionId}/${diskSerial}`);
    if ( selectedOrgId.length > 0 ) {
        url += '?selectedOrgId=' + selectedOrgId;
    }
    return this.http.get(url).pipe(catchError(this.handleError.bind(this)));
  }

  getFdeLicenses(query: IFdeQuery): Observable<IFdeLicenseResponse> {
    let url = this.getUrl(`/api/fde/licenses/query`);
    const pageSize = query['pageSize'];
    if (pageSize === undefined) {
      query['pageSize'] = 50;
    }
    const pageStart = query['pageStart'];
    if (pageStart === undefined) {
      query['pageStart'] = 0;
    }
    if (query['selectedOrgId']) {
        url += `?selectedOrgId=${query['selectedOrgId']}`;
    }
    return this.http.post<IFdeLicenseResponse>(url, query).pipe(catchError(this.handleError.bind(this)));
  }

  getFdeLicenseDetails(selectedOrgId, subscriptionId, workstationId) {
    let url = this.getUrl(`/api/fde/details/${subscriptionId}/${workstationId}`);
    if ( selectedOrgId.length > 0 ) {
        url += '?selectedOrgId=' + selectedOrgId;
    }
    return this.http.get(url).pipe(catchError(this.handleError.bind(this)));
  }

  createFdeLicenseFromRequestCode(selectedOrgId: string, subscription_id: string, request_code: string) {
    let url = this.getUrl('/api/fde/licenses')
    if ( selectedOrgId.length > 0 ) {
      url += '?selectedOrgId=' + selectedOrgId;
    }
    const request = {
      'type': 'requestCode',
      'subscription_id': subscription_id,
      'request_code': request_code,
    }
    return this.http.post(url, request);
  }

  createFdeLicenseFromWorkstationIds(selectedOrgId: string = '', subscription_id, maintenanceEnd, workstationIds) : Observable<any> {
      let url = this.getUrl('/api/fde/licenses/from_workstations')
      if ( selectedOrgId.length > 0 ) {
        url += `?selectedOrgId=${selectedOrgId}`;
      }
      const request = {
        'workstation_ids': workstationIds,
        'subscription_id': subscription_id,
        'maintenance_end': maintenanceEnd
      }
      return this.http.post(url, request);
  }

  addFdeWorkstationIds(selectedOrgId: string = '', addWorkstationsRequest: IAddWorkstationsRequest) : Observable<any> {
      let url = this.getUrl('/api/fde/serials')
      if ( selectedOrgId.length > 0 ) {
        url += `?selectedOrgId=${selectedOrgId}`;
      }
      return this.http.post(url, addWorkstationsRequest);
  }

  deleteFdeLicenses(selectedOrgId: string = '', subscriptionId: string, workstationIds: string[]): Observable<any> {
      let url = this.getUrl('/api/fde/serials/delete')
      if (selectedOrgId.length > 0) {
          url += `?selectedOrgId=${selectedOrgId}`;
      }
      const request = {
          'workstation_ids': workstationIds,
          'subscription_id': subscriptionId
      }
      return this.http.post(url, request);
  }

  addPbaUser(licenseUUid: string, biosSerial: string, user: IPbaUser) {
    const payload = { ...user, biosSerial };
    return this.http.post(
      this.getUrl(`/endpoints/${licenseUUid}/pba-users`),
      payload
    );
  }

  updatePbaUser(licenseUUid: string, user: IPbaUser) {
    return this.http.put(this.getUrl(`/endpoints/${licenseUUid}/pba-users`),user);
  }

  deletePbaUser(licenseUUid: string, user: IPbaUser) {
    const url = this.getUrl(`/endpoints/${licenseUUid}/pba-users`);

    return this.http.delete(url, {
      body: user,
    });
  }

  addFdeUser(licenseUUid: string, biosSerial: string, user: IFdeUser) {
    const payload = { ...user, biosSerial };
    return this.http.post(this.getUrl(`/endpoints/${licenseUUid}/fde-users`), payload);
  }

  updateFdeUser(licenseUUid: string, user: IFdeUser) {
    return this.http.put(this.getUrl(`/endpoints/${licenseUUid}/fde-users`), user);
  }

  deleteFdeUser(licenseUUid: string, user: IFdeUser) {
    const url = this.getUrl(`/endpoints/${licenseUUid}/fde-users`);

    return this.http.delete(url, {
      body: user,
    });
  }

  addEnrollmentCodes(result: IEnrollCodesDialogResult): Observable<IAddEnrollCodesResponse> {
    let url = this.getUrl('/endpoints/enrollment-codes')
    return this.http.post<IAddEnrollCodesResponse>(url, result);
  }

  deleteEnrollmentCodes(request: IDeleteEnrollCodesRequest,) {
    const url = this.getUrl('/endpoints/enrollment-codes');
    return this.http.delete<void>(url, {
      body: request,
    });
  }

  getActionData(endpointUuids: string[],includeUsers = false): Observable<IEndpointActionData[]> {
    const url = this.getUrl('/endpoints/actions-data');
    return this.http.post<IEndpointActionData[]>(
      url,
      {
        endpointUuids,
        includeUsers,
      }
    );
  }

  performConsoleAction(action: ConsoleAction, licenseUUid: string, disk_serial: string, date?: string, range?: number) {
    const url = this.getUrl(`/endpoints/${licenseUUid}/action`);
    return this.http.post<void>(
      url,
      {
        action,
        disk_serial,
        date,
        range,
      }
    );
  }

  performClearConsoleAction(action: ConsoleAction, licenseUUid: string) {
    const url = this.getUrl(`/endpoints/${licenseUUid}/clear-actions`);
    return this.http.post<void>(
      url,
      {
        action
      }
    );
  }

  removeUserConsoleAction(licenseUUid: string, user: IPbaUser, action: ConsoleAction) {
    const url = this.getUrl(`/endpoints/${licenseUUid}/action`);
    return this.http.delete(url, {
      body: {
        ...user,
        actionToCancel: action
      }
    });
  }

  handleError(errResponse: HttpErrorResponse | any): any {
    if ((errResponse instanceof HttpErrorResponse) && errResponse?.error?.status) {
        return new Promise((resolve, reject) => {
            this.translate.get(errResponse.error.status).subscribe(
                (translated) => reject(translated),
                () => reject('Server Error')
            );
        });
    }
    return throwError(errResponse.error || 'Server error');
  }

}
